import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CampaignService } from '../../../services/campaign/campaign.service';
import { Campaign } from '../new-campaign/campaign';
import { Observable } from 'rxjs/Observable';


@Component({
  selector: 'campaign-final-evaluation',
  templateUrl: './final-evaluation.component.html',
  providers: [ CampaignService ]
})
export class FinalEvaluationComponent implements OnInit {

  public campaign: Campaign;
  public budget: number;
  public incrementalSpent: number;
  public roi: number;
  public visibleSerie: string;

  public charts = {
    incrementalSpent: { instance: null, options: null },
    budgetPerType: { instance: null, options: null },
    emissionRedemption: { instance: null, options: null },
    redemptionPerCluster: { instance: null, options: null }
  };


  constructor(
    private campaignService: CampaignService,
    private route: ActivatedRoute
  ) {
    const colors = ['#2c689a', '#3392ba', '#36bedb', '#33ebfd', '#8085e9'];

    this.charts.incrementalSpent.options = {
      chart: { type: 'spline' },
      xAxis: {
        categories: []
      },
      yAxis: {
        title: {
          text: 'Value'
        },
        labels: {
          formatter: function () {
            return this.value + ' €';
          }
        }
      },
      tooltip: {
        crosshairs: true,
        shared: true
      },
      title: { text: '' },
      colors: colors,
      series: [{
        name: '',
        marker: {symbol: 'square'},
        data: []
      }, {
        name: '',
        marker: {symbol: 'diamond'},
        data: []
      }]
    };

    this.charts.budgetPerType.options = {
      chart: {
        type: 'pie',
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false
      },
      title: {
        text: ''
      },
      tooltip: {
        valueSuffix: '%'
      },
      colors: colors,
      series: [{
        name: 'Budget per type',
        colorByPoint: true,
        data: []
      }],
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: false
          },
          showInLegend: true
        }
      }
    };

    this.charts.emissionRedemption.options = {
      chart: { type: 'bar' },
      title: {
        text: ''
      },
      xAxis: {
        categories: [],
        title: { text: '' }
      },
      yAxis: {
        min: 0,
        title: {
          text: '',
          align: 'high'
        },
        labels: {
          overflow: 'justify',
          formatter: function () {
            return this.value + '%';
          }
        }
      },
      tooltip: {
        valueSuffix: '%'
      },
      plotOptions: {
        bar: {
          dataLabels: {
            enabled: true,
            formatter: function () {
              return this.y + '%';
            }
          }
        }
      },
      legend: {
        enabled: false
      },
      series: [
        { name: '', color: colors[0], data: [] },
        { name: '', color: colors[1], data: [] },
        { name: '', color: colors[0], data: [] },
        { name: '', color: colors[1], data: [] }
      ],
    };

    this.charts.redemptionPerCluster.options = {
      chart: { type: 'bar' },
      title: { text: '' },
      xAxis: {
        categories: [],
        title: { text: '' }
      },
      yAxis: {
        min: 0,
        title: {
          text: '',
          align: 'high'
        }
      },
      legend: {
        verticalAlign: 'top'
      },
      plotOptions: {
        series: {
          stacking: 'normal'
        }
      },
      series: [
        { name: '', color: colors[0], data: [] },
        { name: '', color: colors[1], data: [] },
        { name: '', color: colors[2], data: [] },
      ],
    };
  }

  chartInstance(chart: { instance, options }, chartInstance)
  {
    chart.instance = chartInstance;
  }

  ngOnInit()
  {
    const campaignName = this.route.snapshot.queryParams['campaign'];

    Observable.zip(
      this.campaignService.getCampaign(campaignName),
      this.campaignService.getBudgetOverview(campaignName)
    ).subscribe(([campaign, budgetOverview]) => {
      this.campaign = new Campaign(campaign);
      this.budget = budgetOverview.budget;
      this.incrementalSpent = budgetOverview.incrementalSpent;
      this.roi = budgetOverview.roi.toFixed(2);
      this.loadCharts();
    });
  }

  updateSeries(serie: string)
  {
    this.visibleSerie = serie;

    switch(serie)
    {
      case 'emission':
        this.charts.emissionRedemption.instance.series[0].show();
        this.charts.emissionRedemption.instance.series[1].show();
        this.charts.emissionRedemption.instance.series[2].hide();
        this.charts.emissionRedemption.instance.series[3].hide();
        break;

      case 'redemption':
        this.charts.emissionRedemption.instance.series[0].hide();
        this.charts.emissionRedemption.instance.series[1].hide();
        this.charts.emissionRedemption.instance.series[2].show();
        this.charts.emissionRedemption.instance.series[3].show();
        break;
    }
  }

  loadCharts()
  {
    this.campaignService.getCampaignEmissionRedemptionData(this.campaign.name).subscribe(res => {
      this.charts.emissionRedemption.options.xAxis.categories = res.labels;
      this.charts.emissionRedemption.options.chart.height = 125 + res.labels.length * 50;

      // emission
      res.emmisionModel.forEach((data, i) => {
        this.charts.emissionRedemption.options.series[i].name = (data.label == 'Real') ? 'Actual' : data.label;
        this.charts.emissionRedemption.options.series[i].name += ' Emission';
        this.charts.emissionRedemption.options.series[i].data = data.values.map(v => Math.round(parseFloat(v) * 100));
      });

      // redemption
      res.redemptionModel.forEach((data, i) => {
        this.charts.emissionRedemption.options.series[i + 2].name = (data.label == 'Real') ? 'Actual' : data.label;
        this.charts.emissionRedemption.options.series[i + 2].name += ' Redemption';
        this.charts.emissionRedemption.options.series[i + 2].data = data.values.map(v => Math.round(parseFloat(v) * 100));
      });

      this.charts.emissionRedemption.instance.update(this.charts.emissionRedemption.options);
      this.updateSeries('emission');
    });

    this.campaignService.getCampaignRedemptionClusterData(this.campaign.name).subscribe(res => {
      res.campaignRedemptionPerClusterModels.sort((a, b) => {
        return parseInt(a.clusterDescription.charAt(0)) - parseInt(b.clusterDescription.charAt(0));
      });

      res.key.forEach((serieName, i) => {
        let label = res.keyView[i];
        if(label == 'Targeted Customers') {
          label = 'Targeted customer but not redeemed';
        }

        this.charts.redemptionPerCluster.options.series[i].name = label;
        res.campaignRedemptionPerClusterModels.forEach(data => {
          this.charts.redemptionPerCluster.options.xAxis.categories.push(data.clusterDescription);
          this.charts.redemptionPerCluster.options.series[i].data.push(+data[serieName]);
        });
      });

      this.charts.redemptionPerCluster.options.chart.height = 75 + res.campaignRedemptionPerClusterModels.length * 50;
      this.charts.redemptionPerCluster.instance.update(this.charts.redemptionPerCluster.options);
    });

    this.campaignService.getCampaignIncrementalSpentData(this.campaign.name).subscribe(res => {
      this.charts.incrementalSpent.options['series'][1].name= res.key[1];
      this.charts.incrementalSpent.options['series'][0].data = res.campaignIncrementalSpentList[0].map(item => parseFloat(item.dailySpent));
      this.charts.incrementalSpent.options['series'][0].name= res.key[0];
      this.charts.incrementalSpent.options['series'][1].data = res.campaignIncrementalSpentList[1].map(item => parseFloat(item.dailySpent));
      this.charts.incrementalSpent.options['xAxis']['categories'] = res.campaignIncrementalSpentList[1].map(item => item.date);
      this.charts.incrementalSpent.instance.update(this.charts.incrementalSpent.options);
    });

    this.campaignService.getBudgetPerTypeFinalEvalutionData(this.campaign.name).subscribe(res => {
      res.budgetPerType.forEach(data => {
        this.charts.budgetPerType.options.series[0].data.push({
          name: data.label,
          y: parseFloat(data.perc)
        });
      });
      this.charts.budgetPerType.instance.update(this.charts.budgetPerType.options);
    });
  }

}



// WEBPACK FOOTER //
// ./src/app/views/campaign/final-evaluation/final-evaluation.component.ts